import { Practice, hasMergeConflicts } from '@principle-theorem/principle-core';
import {
  IStaffer,
  type ISterilisationCycle,
  type ISterilisationMachine,
  IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  type ITranslationMap,
  type MergeConflictDestinationEntityRecord,
  type ICustomMappingHandler,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IHasSourceIdentifier,
  type IMigratedDataSummary,
  type IPracticeMigration,
  type ISourceEntityHandler,
  type ISourceEntityRecord,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  DocumentReference,
  Firestore,
  FirestoreMigrate,
  PrimaryKey,
  Timestamp,
  WithPrimaryKey,
  WithRef,
  getError,
  toTimestamp,
} from '@principle-theorem/shared';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslationMapHandler } from '../../translation-map';
import { BaseDestinationEntity } from '../base-destination-entity';
import { DestinationEntity } from '../destination-entity';

export const STERILISATION_CYCLE_RESOURCE_TYPE = 'sterilisationCycle';

export const STERILISATION_CYCLE_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: STERILISATION_CYCLE_RESOURCE_TYPE,
    label: 'Sterilisation Cycles',
    description: ``,
  },
});

export interface ISterilisationCycleJobData<
  SterilisationCycleRecord extends ISourceEntityTypes,
> {
  sourceSterilisationCycle: IGetRecordResponse<SterilisationCycleRecord>;
  sterilisationMachineMappings: WithPrimaryKey<
    ITranslationMap<ISterilisationMachine>
  >[];
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
}

export interface ISterilisationCycleDestinationRecord {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  sterilisationCycleRef: DocumentReference<ISterilisationCycle>;
}

export interface ISterilistionCycleRecordMigrationData {
  sterilisationCycleRecord: ISterilisationCycle &
    IHasSourceIdentifier & { createdAt: Timestamp };
  practiceRef: DocumentReference<IPractice>;
}

export abstract class BaseSterilisationCycleDestinationEntity<
  SterilisationCycleRecord extends ISourceEntityTypes,
> extends BaseDestinationEntity<
  ISterilisationCycleDestinationRecord,
  ISterilisationCycleJobData<SterilisationCycleRecord>,
  ISterilistionCycleRecordMigrationData
> {
  destinationEntity = STERILISATION_CYCLE_DESTINATION_ENTITY;
  abstract sterilisationCycleSourceEntity: ISourceEntityHandler<SterilisationCycleRecord>;
  abstract sterilisationMachineMappings: ICustomMappingHandler<ISterilisationMachine>;

  get sourceCountComparison(): ISourceEntityHandler<SterilisationCycleRecord> {
    return this.sterilisationCycleSourceEntity;
  }

  sourceCountDataAccessor(
    data: ISterilisationCycleJobData<SterilisationCycleRecord>
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourceSterilisationCycle.record.id;
  }

  getDestinationEntityRecordUid(
    data: ISterilisationCycleJobData<SterilisationCycleRecord>
  ): string {
    return data.sourceSterilisationCycle.record.uid;
  }

  getMigratedData$(
    record: IDestinationEntityRecord<ISterilisationCycleDestinationRecord>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return Firestore.doc$(record.data.sterilisationCycleRef).pipe(
      map((sterilisationCycle) => [
        { label: 'Sterilisation Cycle', data: sterilisationCycle },
      ])
    );
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: ISterilisationCycleJobData<SterilisationCycleRecord>,
    _migrationData: ISterilistionCycleRecordMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourceSterilisationCycle.record.uid,
      label: jobData.sourceSterilisationCycle.record.label,
      sourceRef: jobData.sourceSterilisationCycle.record.id,
      status: DestinationEntityRecordStatus.MergeConflict,
    };
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    data: ISterilistionCycleRecordMigrationData
  ): Promise<ISterilistionCycleRecordMigrationData | undefined> {
    const existingSterilisationCycleRef =
      await translationMap.getDestination<ISterilisationCycle>(
        data.sterilisationCycleRecord.sourceIdentifier,
        STERILISATION_CYCLE_RESOURCE_TYPE
      );
    if (!existingSterilisationCycleRef) {
      return;
    }

    try {
      const existingSterilisationCycle = await Firestore.getDoc(
        existingSterilisationCycleRef
      );
      const hasMergeConflict = hasMergeConflicts(
        data.sterilisationCycleRecord,
        existingSterilisationCycle
      );

      if (hasMergeConflict) {
        return {
          ...data,
          sterilisationCycleRecord: {
            ...existingSterilisationCycle,
            sourceIdentifier: data.sterilisationCycleRecord.sourceIdentifier,
          },
        };
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        `Error checking for merge conflict for sterilisation cycle: ${getError(
          error
        )}`
      );
    }
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: ISterilisationCycleJobData<SterilisationCycleRecord>,
    migrationData: ISterilistionCycleRecordMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const existingCycleRef = await translationMap.getDestination(
        migrationData.sterilisationCycleRecord.sourceIdentifier,
        STERILISATION_CYCLE_RESOURCE_TYPE
      );

      const cycleRef = await FirestoreMigrate.upsertDoc(
        Practice.sterilisationCycleCol({ ref: migrationData.practiceRef }),
        migrationData.sterilisationCycleRecord,
        existingCycleRef?.id
      );

      if (!existingCycleRef) {
        await translationMap.upsert({
          sourceIdentifier:
            migrationData.sterilisationCycleRecord.sourceIdentifier,
          destinationIdentifier: cycleRef,
          resourceType: STERILISATION_CYCLE_RESOURCE_TYPE,
        });
      }

      return this._buildSuccessResponse(
        data.sourceSterilisationCycle,
        cycleRef
      );
    } catch (error) {
      return this._buildErrorResponse(
        data.sourceSterilisationCycle,
        getError(error)
      );
    }
  }

  protected _buildErrorResponse(
    steriliserCycle: IGetRecordResponse<SterilisationCycleRecord>,
    errorMessage?: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: steriliserCycle.record.uid,
      label: steriliserCycle.record.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: steriliserCycle.record.id,
      errorMessage: errorMessage ?? `Could not migrate sterilisation cycle`,
      failData: {},
    };
  }

  protected _buildSuccessResponse(
    record: IGetRecordResponse<SterilisationCycleRecord>,
    sterilisationCycleRef: DocumentReference<ISterilisationCycle>
  ): IDestinationEntityRecord<ISterilisationCycleDestinationRecord> {
    return {
      uid: record.record.uid,
      label: record.record.label,
      data: {
        sourceRef: record.record.id,
        sterilisationCycleRef,
      },
      sourceRef: record.record.id,
      status: DestinationEntityRecordStatus.Migrated,
      migratedAt: toTimestamp(),
    };
  }
}
